import { createAuthAxios, handleApiError, getAuthToken } from "../../utils/apiUtils";
import { API_BASE_URL } from "../../config/apiConfig";

/**
 * Room Profile Photo Service
 * Handles photo upload, update, and deletion for research rooms
 */

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "gif"];

class RoomPhotoService {
  /**
   * Validate file before upload
   * @param {File} file - The file to validate
   * @returns {{ valid: boolean, error?: string }}
   */
  validateFile(file) {
    if (!file) {
      return { valid: false, error: "No file selected" };
    }

    if (file.size > MAX_FILE_SIZE) {
      return { 
        valid: false, 
        error: `File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB` 
      };
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      return { 
        valid: false, 
        error: "File type not supported. Use JPG, PNG, WebP, or GIF" 
      };
    }

    // Check file extension as additional validation
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
      return {
        valid: false,
        error: "Invalid file extension. Use .jpg, .jpeg, .png, .webp, or .gif"
      };
    }

    return { valid: true };
  }

  /**
   * Get pre-signed upload URL from backend
   * @param {string} roomId - The room ID
   * @param {string} filename - Original filename
   * @param {string} contentType - MIME type of the file
   */
  async getUploadUrl(roomId, filename, contentType) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `${API_BASE_URL}/research-rooms/${roomId}/photo/upload-url`,
        { filename, contentType }
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Upload file directly to S3 using pre-signed URL
   * @param {string} uploadUrl - Pre-signed S3 URL
   * @param {File} file - File to upload
   */
  async uploadToS3(uploadUrl, file) {
    try {
      const response = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      if (!response.ok) {
        throw new Error("Failed to upload file to storage");
      }

      return true;
    } catch (error) {
      throw new Error(error.message || "Failed to upload file to storage");
    }
  }

  /**
   * Update room's profile photo path in database
   * @param {string} roomId - The room ID
   * @param {string} photoKey - S3 key for the photo
   */
  async updateRoomPhoto(roomId, photoKey) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.patch(
        `${API_BASE_URL}/research-rooms/${roomId}/photo`,
        { profilePhoto: `/${photoKey}` }
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Delete room's profile photo
   * @param {string} roomId - The room ID
   */
  async deleteRoomPhoto(roomId) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        `${API_BASE_URL}/research-rooms/${roomId}/photo`
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get room photo info with signed URL
   * @param {string} roomId - The room ID
   */
  async getRoomPhotoInfo(roomId) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        `${API_BASE_URL}/research-rooms/${roomId}/photo`
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Complete upload flow: validate, get URL, upload to S3, update database
   * @param {string} roomId - The room ID
   * @param {File} file - File to upload
   * @returns {Promise<object>} Updated room data with signed URL
   */
  async uploadRoomPhoto(roomId, file) {
    // Step 1: Validate file
    const validation = this.validateFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Step 2: Get pre-signed upload URL
    const { uploadUrl, key } = await this.getUploadUrl(
      roomId,
      file.name,
      file.type
    );

    // Step 3: Upload file directly to S3
    await this.uploadToS3(uploadUrl, file);

    // Step 4: Update room with photo path
    const result = await this.updateRoomPhoto(roomId, key);

    return result;
  }

  /**
   * Batch get signed URLs for multiple rooms
   * @param {string[]} roomIds - Array of room IDs
   */
  async batchGetSignedUrls(roomIds) {
    try {
      if (!roomIds || roomIds.length === 0) {
        return { success: true, data: { rooms: [] } };
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `${API_BASE_URL}/research-rooms/photos/batch-signed-urls`,
        { roomIds }
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
}

export default new RoomPhotoService();
